import React from 'react'
import type { SelectableCardProps } from './SelectableCard.types'
import {
  SELECTABLE_CARD_DEMO_META,
  SELECTABLE_CARD_DEMO_SUBTITLE,
  SELECTABLE_CARD_DEMO_TAGS,
  SELECTABLE_CARD_DEMO_TITLE,
} from './SelectableCard.types'
import './SelectableCard.css'

const SelectableCard: React.FC<SelectableCardProps> = ({
  title,
  subtitle,
  tags,
  meta,
  actions,
  selected = false,
  disabled = false,
  density = 'compact',
  className = '',
  darkMode = false,
  onClick,
  previewState,
}) => {
  const isPreview = previewState != null

  const resolvedTitle =
    title ?? (isPreview ? SELECTABLE_CARD_DEMO_TITLE : undefined)

  const resolvedSubtitle =
    subtitle ??
    (previewState === 'with-tags'
      ? undefined
      : isPreview
        ? SELECTABLE_CARD_DEMO_SUBTITLE
        : undefined)

  const resolvedTags =
    tags ??
    (previewState === 'with-tags' || previewState === 'with-meta'
      ? SELECTABLE_CARD_DEMO_TAGS
      : undefined)

  const resolvedMeta =
    meta ??
    (previewState === 'with-meta' ? SELECTABLE_CARD_DEMO_META : undefined)

  const resolvedSelected = selected || previewState === 'selected'
  const resolvedDisabled = disabled || previewState === 'disabled'

  const resolvedDensity =
    previewState === 'with-tags' ||
    previewState === 'with-meta' ||
    previewState === 'with-actions'
      ? 'comfortable'
      : density

  const resolvedActions = (() => {
    // `null` forces no actions (showcase toggle); `undefined` allows preview demos.
    if (actions === null) {
      return undefined
    }

    if (actions != null) {
      return actions
    }

    if (previewState === 'with-actions' || previewState === 'with-meta') {
      return (
        <>
          <button
            type="button"
            className="ds-selectable-card__action"
            tabIndex={-1}
            aria-label="Edit"
          >
            <i className="mgc mgc_pencil_fill" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="ds-selectable-card__action ds-selectable-card__action--danger"
            tabIndex={-1}
            aria-label="Delete"
          >
            <i className="mgc mgc_delete_2_line" aria-hidden="true" />
          </button>
        </>
      )
    }

    return undefined
  })()

  const isInteractive = onClick != null && !resolvedDisabled && !isPreview

  const classNames = [
    'ds-selectable-card',
    `ds-selectable-card--${resolvedDensity}`,
    resolvedSelected ? 'ds-selectable-card--selected' : '',
    resolvedDisabled ? 'ds-selectable-card--disabled' : '',
    isInteractive ? 'ds-selectable-card--interactive' : '',
    resolvedActions ? 'ds-selectable-card--has-actions' : '',
    darkMode ? 'ds-selectable-card--dark' : '',
    isPreview ? 'ds-selectable-card--preview' : '',
    previewState ? `ds-selectable-card--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const handleClick = (): void => {
    if (!isInteractive) {
      return
    }

    onClick?.()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!isInteractive) {
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick?.()
    }
  }

  return (
    <div
      className={classNames}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-pressed={isInteractive ? resolvedSelected : undefined}
      aria-disabled={resolvedDisabled || undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {resolvedActions ? (
        <div
          className="ds-selectable-card__actions"
          onClick={(event) => {
            event.stopPropagation()
          }}
          onKeyDown={(event) => {
            event.stopPropagation()
          }}
        >
          {resolvedActions}
        </div>
      ) : null}

      {resolvedTitle ? (
        <p className="ds-selectable-card__title" title={resolvedTitle}>
          {resolvedTitle}
        </p>
      ) : null}

      {resolvedSubtitle ? (
        <p className="ds-selectable-card__subtitle">{resolvedSubtitle}</p>
      ) : null}

      {resolvedTags && resolvedTags.length > 0 ? (
        <div className="ds-selectable-card__tags">
          {resolvedTags.map((tag) => (
            <span key={tag} className="ds-selectable-card__tag">
              {tag}
            </span>
          ))}
        </div>
      ) : null}

      {resolvedMeta ? (
        <p className="ds-selectable-card__meta">{resolvedMeta}</p>
      ) : null}
    </div>
  )
}

export default SelectableCard
